import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/lib/auth';
import { useEffectiveRole } from '@/features/impersonation/useImpersonation';
import { SELECTED_LOCATION_KEY_PREFIX } from './storageKeys';
import { trpc } from '@/lib/trpc';
import { canSwitchLocations, resolveInitialLocation } from './locationScopeLogic';

/**
 * Multi-location experience layer (2026-06-22).
 *
 * A single source of truth for "which shop is this user viewing right now",
 * surviving navigation (mounted at app root, not per-route). The top-bar
 * `<LocationSwitcher />` writes it; location-scoped pages read it to re-scope
 * their queries; the `<LocationChip />` echoes it on the page so the user always
 * knows their current shop.
 *
 * Company is auto-resolved from the user's tenant — there is no company picker.
 * `null` == "All Shops" (company-wide). Reachable shops are every shop in the
 * company (reach-across); a future per-manager hard-lock would narrow this set
 * via one config flag, but the default today is reach-across.
 */

export interface LocationScopeOption {
  id: string;
  name: string;
  truckCount: number;
}

interface LocationScopeValue {
  /** Currently scoped shop id. `null` = "All Shops" (company-wide). */
  selectedLocationId: string | null;
  setSelectedLocation: (id: string | null) => void;
  /** Reachable shops for the company, name-sorted, each with its truck count. */
  locations: LocationScopeOption[];
  /** The user's home shop id, or null (admin / leadership often have none). */
  homeLocationId: string | null;
  /** Display name of the current scope; null when "All Shops". */
  currentLocationName: string | null;
  /** True when this role may switch shops (manager / admin / leadership). */
  canSwitch: boolean;
  isLoading: boolean;
}

const LocationScopeContext = createContext<LocationScopeValue | null>(null);

const storageKey = (userId: string) => `${SELECTED_LOCATION_KEY_PREFIX}${userId}`;

export function LocationScopeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const canSwitch = canSwitchLocations(effectiveRole);

  // Only the switcher roles fetch the shop list; techs never call it.
  const locationsQuery = trpc.manager.locationsForCompany.useQuery(undefined, {
    enabled: canSwitch,
    staleTime: 5 * 60_000,
  });
  const locations = useMemo<LocationScopeOption[]>(
    () => locationsQuery.data ?? [],
    [locationsQuery.data],
  );

  // Cross-tenant cache hygiene: drop the cached shop list when the company
  // changes — e.g. signing out of company A and into company B in the SAME
  // browser tab. This query is keyed the same for every user, so without a
  // reset the previous company's shops + truck counts would show until the
  // staleTime elapsed. The server query is already scoped to ctx.user.companyId
  // (no server leak) — this only hardens the client cache. The first mount is
  // skipped so initial load doesn't double-fetch.
  const utils = trpc.useUtils();
  const prevCompanyRef = useRef<string | null>(null);
  useEffect(() => {
    const next = user?.companyId ?? null;
    if (prevCompanyRef.current !== null && prevCompanyRef.current !== next) {
      void utils.manager.locationsForCompany.reset();
    }
    prevCompanyRef.current = next;
  }, [user?.companyId, utils]);

  const homeLocationId = user?.primaryLocationId ?? null;

  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [seededUserId, setSeededUserId] = useState<string | null>(null);

  // Seed once per signed-in user (re-seed when the user changes). A stored
  // choice wins; else manager → home shop; else → All Shops.
  useEffect(() => {
    if (!user || seededUserId === user.id) return;
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(storageKey(user.id));
    } catch {
      stored = null;
    }
    setSelectedLocationId(
      resolveInitialLocation({
        role: effectiveRole,
        primaryLocationId: user.primaryLocationId,
        stored,
      }),
    );
    setSeededUserId(user.id);
  }, [user, effectiveRole, seededUserId]);

  const setSelectedLocation = useCallback(
    (id: string | null) => {
      setSelectedLocationId(id);
      if (user) {
        try {
          localStorage.setItem(storageKey(user.id), id ?? '');
        } catch {
          // Storage unavailable — the selection still lives in memory for this session.
        }
      }
    },
    [user],
  );

  // Guard against a stored shop that's no longer reachable (data drift): once
  // the shop list loads, fall back to All Shops if the selection isn't in it.
  useEffect(() => {
    if (selectedLocationId === null || locations.length === 0) return;
    if (!locations.some((l) => l.id === selectedLocationId)) {
      setSelectedLocationId(null);
    }
  }, [locations, selectedLocationId]);

  const currentLocationName =
    selectedLocationId === null
      ? null
      : (locations.find((l) => l.id === selectedLocationId)?.name ?? null);

  const value = useMemo<LocationScopeValue>(
    () => ({
      selectedLocationId,
      setSelectedLocation,
      locations,
      homeLocationId,
      currentLocationName,
      canSwitch,
      isLoading: canSwitch && locationsQuery.isLoading,
    }),
    [
      selectedLocationId,
      setSelectedLocation,
      locations,
      homeLocationId,
      currentLocationName,
      canSwitch,
      locationsQuery.isLoading,
    ],
  );

  return <LocationScopeContext.Provider value={value}>{children}</LocationScopeContext.Provider>;
}

/**
 * Inert default used when there is no provider above — an isolated component
 * test, or a surface mounted outside the app shell. Degrades to "no switcher,
 * All-Shops (company-wide) scope" rather than throwing. The provider is mounted
 * unconditionally at the app root (App.tsx), so this only ever applies to
 * tests/stray renders, where company-wide is the correct, non-crashing default.
 */
const FALLBACK_SCOPE: LocationScopeValue = {
  selectedLocationId: null,
  setSelectedLocation: () => {},
  locations: [],
  homeLocationId: null,
  currentLocationName: null,
  canSwitch: false,
  isLoading: false,
};

export function useLocationScope(): LocationScopeValue {
  return useContext(LocationScopeContext) ?? FALLBACK_SCOPE;
}
